import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { Helmet } from 'react-helmet';
import { FaArrowLeft, FaUser, FaBook, FaClipboardCheck, FaTrophy, FaChartLine, FaChild } from 'react-icons/fa';
import { fetchStudentStatistics, setSelectedChildId } from '../../redux/slices/studentStatisticsSlice';
import styles from './LevelIndicator.module.css';

const LevelIndicator = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useDarkMode();
  const { currentUser } = useSelector((state) => state.auth);
  const { statistics, loading, error, selectedChildId } = useSelector((state) => state.studentStatistics);
  
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  
  // Check user role
  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isParent = userRole === "parent";
  const isTeacher = userRole === "teacher";
  const isStudent = userRole === "student";
  
  // Redirect if not authorized
  useEffect(() => {
    if (!isParent && !isTeacher && !isStudent) {
      navigate('/');
    }
  }, [userRole, navigate]);
  
  // Mock children data for parent/teacher - replace with real API call
  useEffect(() => {
    if (isParent || isTeacher) {
      // Simulate children data - replace with real API call
      const mockChildren = [
        { id: 525, name: 'أحمد محمد', grade: 'الصف الثالث الثانوي' },
        { id: 526, name: 'فاطمة أحمد', grade: 'الصف الثاني الثانوي' },
        { id: 527, name: 'محمد علي', grade: 'الصف الأول الثانوي' }
      ];
      setChildren(mockChildren);
      
      // Set first child as default
      if (mockChildren.length > 0) {
        setSelectedChild(mockChildren[0]);
        dispatch(setSelectedChildId(mockChildren[0].id));
      }
    }
  }, [isParent, isTeacher, dispatch]);
  
  // Fetch statistics when child changes or for student
  useEffect(() => {
    if (isStudent) {
      // For student, fetch their own statistics (no child_id needed)
      dispatch(fetchStudentStatistics());
    } else if (selectedChild && (isParent || isTeacher)) {
      // For parent/teacher, fetch specific child statistics
      dispatch(fetchStudentStatistics(selectedChild.id));
    }
  }, [selectedChild, dispatch, isParent, isTeacher, isStudent]);
  
  const handleChildChange = (childId) => {
    const child = children.find(c => c.id === parseInt(childId));
    if (child) {
      setSelectedChild(child);
      dispatch(setSelectedChildId(child.id));
    }
  };
  
  const formatPercentage = (percent) => {
    return `${percent.toFixed(1)}%`;
  };
  
  const getProgressColor = (percent) => {
    if (percent >= 80) return '#28a745'; // Green
    if (percent >= 60) return '#ffc107'; // Yellow
    if (percent >= 40) return '#fd7e14'; // Orange
    return '#dc3545'; // Red
  };
  
  const getProgressLevel = (percent) => {
    if (percent >= 90) return 'ممتاز';
    if (percent >= 80) return 'جيد جداً';
    if (percent >= 70) return 'جيد';
    if (percent >= 60) return 'مقبول';
    if (percent >= 50) return 'ضعيف';
    return 'ضعيف جداً';
  };

  if (!isParent && !isTeacher && !isStudent) {
    return null;
  }

  return (
    <div className={`${styles.levelIndicatorContainer} ${isDarkMode ? styles.darkMode : ''}`}>
      <Helmet>
        <title>مؤشر المستوى - علمني</title>
        <meta name="description" content="مؤشر مستوى الطالب - عرض الإحصائيات والتقدم" />
        <meta name="keywords" content="مؤشر مستوى, إحصائيات, تقدم الطالب, علمني" />
      </Helmet>

      {/* Header */}
      <div className={styles.levelHeader}>
        <button 
          className={styles.levelBackBtn}
          onClick={() => navigate('/')}
        >
          <FaArrowLeft />
          الرئيسية
        </button>
        <h1 className={styles.levelTitle}>
          <FaTrophy className={styles.headerIcon} />
          مؤشر المستوى
        </h1>
        <div className={styles.headerSubtitle}>
          {isStudent ? 'تتبع تقدمك الأكاديمي' : 'تتبع تقدم الأبناء الأكاديمي'}
        </div>
      </div>

      {/* Child Selection - Only for Parent/Teacher */}
      {(isParent || isTeacher) && (
        <div className={styles.childSelectionSection}>
          <div className={styles.childSelectionContainer}>
            <div className={styles.childSelectionHeader}>
              <FaChild className={styles.childSelectionIcon} />
              <h2>اختر الطالب</h2>
            </div>
            <select 
              className={styles.childSelectionDropdown}
              value={selectedChild?.id || ''}
              onChange={(e) => handleChildChange(e.target.value)}
            >
              {children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.name} - {child.grade}
                </option>
              ))}
            </select>
          </div>
        </div>
            )}

      {/* Statistics Display */}
      {((isStudent) || (selectedChild && (isParent || isTeacher))) && (
        <div className={styles.statisticsSection}>
          <div className={styles.statisticsContainer}>

            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>جاري تحميل الإحصائيات...</p>
              </div>
            ) : error ? (
              <div className={styles.errorContainer}>
                <p>حدث خطأ في تحميل الإحصائيات</p>
                <button 
                  className={styles.retryBtn}
                  onClick={() => {
                    if (isStudent) {
                      dispatch(fetchStudentStatistics());
                    } else {
                      dispatch(fetchStudentStatistics(selectedChild.id));
                    }
                  }}
                >
                  إعادة المحاولة
                </button>
              </div>
            ) : statistics ? (
              <div className={styles.statsGrid}>
                {/* Lessons Progress */}
                <div className={`${styles.statCard} ${styles.lessonsCard}`}>
                  <div className={styles.statIcon}>
                    <FaBook />
                  </div>
                  <div className={styles.statContent}>
                    <h3>حضور الدروس</h3>
                    <div className={styles.statNumber}>
                      {statistics.lessons_count}
                    </div>
                    <div className={styles.statProgress}>
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill}
                          style={{ 
                            width: `${statistics.lessons_percent}%`,
                            backgroundColor: getProgressColor(statistics.lessons_percent)
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressText}>
                        {formatPercentage(statistics.lessons_percent)}
                      </span>
                    </div>
                    <div className={styles.statDescription}>
                      المستوى: {getProgressLevel(statistics.lessons_percent)}
                    </div>
                  </div>
                </div>

                {/* Tests Progress */}
                <div className={`${styles.statCard} ${styles.testsCard}`}>
                  <div className={styles.statIcon}>
                    <FaClipboardCheck />
                  </div>
                  <div className={styles.statContent}>
                    <h3>الاختبارات</h3>
                    <div className={styles.statNumber}>
                      {statistics.tests_count}
                    </div>
                    <div className={styles.statProgress}>
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill}
                          style={{ 
                            width: `${statistics.tests_percent}%`,
                            backgroundColor: getProgressColor(statistics.tests_percent)
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressText}>
                        {formatPercentage(statistics.tests_percent)}
                      </span>
                    </div>
                    <div className={styles.statDescription}>
                      المستوى: {getProgressLevel(statistics.tests_percent)}
                    </div>
                  </div>
                </div>

                {/* Total Degree */}
                <div className={`${styles.statCard} ${styles.scoreCard}`}>
                  <div className={styles.statIcon}>
                    <FaTrophy />
                  </div>
                  <div className={styles.statContent}>
                    <h3>إجمالي الدرجات</h3>
                    <div className={styles.statNumber}>
                      {statistics.total_degree}
                    </div>
                    <div className={styles.statProgress}>
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill}
                          style={{ 
                            width: `${statistics.degree_percent}%`,
                            backgroundColor: getProgressColor(statistics.degree_percent)
                          }}
                        ></div>
                      </div>
                      <span className={styles.progressText}>
                        {formatPercentage(statistics.degree_percent)}
                      </span>
                    </div>
                    <div className={styles.statDescription}>
                      المستوى: {getProgressLevel(statistics.degree_percent)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.noData}>
                <p>لا توجد بيانات متاحة</p>
              </div>
            )}

            {/* Summary Section - Only show when statistics are available */}
            {statistics && (
              <div className={styles.insightsSection}>
                <div className={styles.insightsHeader}>
                  <FaChartLine className={styles.summaryIcon} />
                  <h2>ملخص الأداء</h2>
                </div>
                <div className={styles.insightsGrid}>
                  <div className={styles.insightCard}>
                    <h4>أفضل أداء</h4>
                    <div className={styles.insightValue}>
                      {statistics.tests_percent > statistics.lessons_percent && statistics.tests_percent > statistics.degree_percent 
                        ? 'الاختبارات' 
                        : statistics.lessons_percent > statistics.degree_percent 
                          ? 'حضور الدروس' 
                          : 'إجمالي الدرجات'
                      }
                    </div>
                  </div>
                  <div className={styles.insightCard}>
                    <h4>المستوى العام</h4>
                    <div className={styles.insightValue}>
                      {getProgressLevel((statistics.lessons_percent + statistics.tests_percent + statistics.degree_percent) / 3)}
                    </div>
                  </div>
                  <div className={styles.insightCard}>
                    <h4>نسبة التقدم</h4>
                    <div className={styles.insightValue}>
                      {formatPercentage((statistics.lessons_percent + statistics.tests_percent + statistics.degree_percent) / 3)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelIndicator;
